import { AppComponent } from 'app/app.component';
import { appConfig } from 'app/app.config';
import { licenseKey } from 'devextreme-license';
import config from 'devextreme/core/config';
import DataGrid from 'devextreme/ui/data_grid';
import DateBox from 'devextreme/ui/date_box';
import DropDownBox from 'devextreme/ui/drop_down_box';
import DropDownButton from 'devextreme/ui/drop_down_button';
import SelectBox from 'devextreme/ui/select_box';
import TagBox from 'devextreme/ui/tag_box';
import {
    installVlpGridHeaderTooltips,
    vlpDataGridDefaultOptions,
} from 'app/core/grid/vlp-data-grid-defaults';
import { createVlpDataGridSearchController } from 'app/core/grid/vlp-data-grid-search';
import { installVlpPopupDefaults } from 'app/core/ui/vlp-popup-defaults';

import { bootstrapApplication } from '@angular/platform-browser';

config({ licenseKey });

/** Height of the grid search box / search + reset buttons, matched to the app's toolbar buttons. */
const SEARCH_ITEM_HEIGHT = 40;

/**
 * DevExtreme stringifies an object item when a DropDownButton has no displayExpr, producing
 * "[object Object]" in popup labels/tooltips. Keep the default compatible with string items and
 * with the label conventions currently used by action menus across the application.
 */
function popupMenuItemText(item: unknown): string {
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    if (!item || typeof item !== 'object') return '';

    const record = item as Record<string, unknown>;
    for (const property of [
        'text', 'label', 'title', 'name', 'displayName',
        'Text', 'Label', 'Title', 'Name', 'DisplayName',
    ]) {
        const value = record[property];
        if (typeof value === 'string' && value.trim()) return value;
        if (typeof value === 'number') return String(value);
    }

    // A malformed translation may put an object in `text`; showing its key is more useful than
    // leaking JavaScript's default object representation into the UI.
    for (const property of ['textKey', 'labelKey', 'titleKey', 'id', 'code', 'Code']) {
        const value = record[property];
        if (typeof value === 'string' && value.trim()) return value;
        if (typeof value === 'number') return String(value);
    }

    return '';
}

function gridOptOut(event: any, attribute: string): boolean {
    const rootElement = event.element?.get?.(0) ?? event.element;
    return rootElement?.getAttribute?.(attribute) === 'off';
}

function gridAttributeEnabled(event: any, attribute: string): boolean {
    const rootElement = event.element?.get?.(0) ?? event.element;
    return rootElement?.getAttribute?.(attribute) === 'on';
}

/**
 * Grid opt-out cho nút "X" (reset) đứng riêng cạnh nút tìm kiếm: khai báo
 * `data-vlp-search-reset="off"` trên <dx-data-grid>. Ô tìm kiếm đã có sẵn nút xoá bên trong
 * (showClearButton) nên trên một số trang nút X rời là dư thừa. Đọc từ thuộc tính DOM vì
 * DataGrid không có option riêng cho các item toolbar do app tự chèn.
 */
function shouldHideSearchResetButton(event: any): boolean {
    return gridOptOut(event, 'data-vlp-search-reset');
}

function configureExplicitGridSearch(event: any): void {
    const grid = event.component;
    const items = event.toolbarOptions?.items as any[] | undefined;
    if (!grid || !items) return;
    // SL-367: KHÔNG ép filterRow.visible=true trong onToolbarPreparing nữa. App-wide default
    // (DataGrid.defaultOptions bên dưới) đã bật filterRow cho grid thông thường; việc ép lại ở đây
    // khiến grid CHỦ ĐỘNG tắt filterRow ([filterRow]="{ visible: false }", vd bảng "Biểu giá theo
    // trọng lượng và vùng" 50+ cột trong popup "Chi tiết giá bán") vẫn bị bật → mỗi ô filter render
    // một icon "apply filter" (applyFilter:'onClick'), dồn thành hàng loạt icon tìm kiếm lặp lại sai
    // layout. Tôn trọng lựa chọn tắt filterRow của từng grid (grid nào không khai báo vẫn giữ default = bật).
    // applyFilter is NOT forced here: the app-wide default (DataGrid.defaultOptions below) stays
    // 'onClick' so remote grids never fire a server filter per keystroke, but a grid may opt into
    // live filtering by declaring filterRow.applyFilter='auto' (e.g. small client-side array grids).
    // Grid opt-out cho bộ lọc trên đầu cột (phễu headerFilter): khai báo `data-vlp-header-filter="off"`.
    // Cần một escape hatch riêng vì dòng dưới CHỦ ĐỘNG bật lại headerFilter, nên `[headerFilter]` khai
    // báo trên template không có tác dụng. Dùng cho lưới chỉ đọc vài dòng, nơi ô tìm kiếm là đủ và phễu
    // chỉ chiếm chỗ của caption (caption phải nằm gọn một dòng).
    const headerFilterVisible = !gridOptOut(event, 'data-vlp-header-filter');
    if (grid.option('headerFilter.visible') !== headerFilterVisible) {
        grid.option('headerFilter.visible', headerFilterVisible);
    }
    // Small lookup dropdowns may explicitly opt into DevExtreme's native live search. Other grids use
    // the application's debounced controller below so typing filters automatically without flooding APIs.
    if (gridAttributeEnabled(event, 'data-vlp-live-search')) return;
    const searchIndex = items.findIndex((item) => item?.name === 'searchPanel');
    const original = searchIndex >= 0
        ? items[searchIndex]
        : { location: 'after', options: { width: 280 } };
    // DevExtreme's own searchPanel toolbar item is defined with a `template` (grid_core/search →
    // _prepareSearchItem), and a collection widget resolves `item.template` BEFORE `widget`/`options`
    // (collection_widget.base → _getItemTemplateName returns data.template when present). Spreading the
    // original item therefore carried `template` along and silently discarded everything declared below:
    // the box rendered was DevExtreme's own live-search editor, so `height`, `width`, `value`,
    // `onEnterKey` and `showClearButton` never applied. Drop `template` so this dxTextBox is what renders.
    const { template: _replacedByOwnEditor, ...originalWithoutTemplate } = original as any;
    const searchController = createVlpDataGridSearchController(grid);
    const searchItems: any[] = [
        {
            ...originalWithoutTemplate,
            name: 'explicitSearchText',
            widget: 'dxTextBox',
            options: {
                ...original.options,
                value: searchController.value,
                valueChangeEvent: 'input',
                // SL-318: no mode:'search' — type="search" adds the browser-native clear "X" on top of
                // DevExtreme's showClearButton "X", giving two clear buttons. Default text mode keeps only
                // the themed showClearButton "X", which is wired to the editor value.
                showClearButton: true,
                // Toolbar baseline: the app's toolbar buttons are declared height="40" (the dominant
                // convention), while a DevExtreme editor defaults to ~36px — so the search box sat lower
                // than the "Thêm mới" button next to it. Pin both search items to the same 40.
                height: SEARCH_ITEM_HEIGHT,
                // Honour what each grid declares on <dxo-search-panel> (width/placeholder). Those used to
                // reach the editor through DevExtreme's own template; now that this item replaces it, read
                // them from the grid options or the popup's 400px search box would silently shrink to 280
                // and lose its placeholder.
                width: original.options?.width ?? grid.option('searchPanel.width') ?? 280,
                placeholder: grid.option('searchPanel.placeholder') ?? '',
                onValueChanged: (change: any) => searchController.update(change.value),
                onEnterKey: searchController.apply,
            },
        },
        {
            name: 'applyExplicitSearch',
            location: original.location ?? 'after',
            widget: 'dxButton',
            options: {
                icon: 'search',
                type: 'default',
                stylingMode: 'contained',
                height: SEARCH_ITEM_HEIGHT,
                onClick: searchController.apply,
            },
        },
    ];
    if (!shouldHideSearchResetButton(event)) {
        searchItems.push({
            name: 'resetExplicitSearch',
            location: original.location ?? 'after',
            widget: 'dxButton',
            options: {
                icon: 'clear',
                stylingMode: 'outlined',
                height: SEARCH_ITEM_HEIGHT,
                onClick: () => {
                    searchController.reset();
                    grid.repaint();
                },
            },
        });
    }
    items.splice(searchIndex >= 0 ? searchIndex : 0, searchIndex >= 0 ? 1 : 0, ...searchItems);
}

// Default for local/read-only grids. Remote grids may override this until their
// gRPC endpoint explicitly supports the corresponding column filters.
DataGrid.defaultOptions({
    options: {
        ...vlpDataGridDefaultOptions,
        filterRow: { visible: true, applyFilter: 'onClick' },
        headerFilter: { visible: true },
        onToolbarPreparing: configureExplicitGridSearch,
        // Popup "Chọn cột" (nút 3 gạch ngang ở toolbar lưới) mở ra mỗi lần một vị trí khác nhau.
        // Hai nguyên nhân:
        //   1. Vị trí mặc định của DevExtreme neo vào GÓC PHẢI-DƯỚI của vùng dữ liệu, không phải vào nút
        //      bấm (grid_core/column_chooser → getPosition(): my/at 'right bottom' of rowsView,
        //      collision 'fit'). Chiều cao vùng dữ liệu thay đổi theo số dòng / page size / master-detail
        //      đang mở, nên popup rơi mỗi lúc một chỗ và bị 'fit' kéo lại khi tràn viewport.
        //   2. Popup này do chính DataGrid tạo và KHÔNG khai báo container ⇒ gặp đúng lỗi cộng dồn hai
        //      transform mô tả ở khối dropDownOptions bên dưới (popup văng ra ngoài vùng nhìn thấy).
        // Neo vào chính nút đã mở popup + ghim container vào <body> để vị trí luôn xác định.
        // defaultOptions deep-merge với <dxo-column-chooser> khai báo ở từng lưới (OptionManager set
        // theo merge vì 'columnChooser' không nằm trong optionsByReference) nên enabled/mode/allowSearch
        // của từng trang vẫn giữ nguyên.
        // Selector dùng được vì tại một thời điểm chỉ có 1 nút column chooser trong DOM (trang nhiều lưới
        // đều gate bằng *ngIf theo tab). Nếu sau này có 2 lưới cùng bật trên 1 trang, popup neo vào nút
        // đầu tiên — vẫn xác định, không nhảy.
        columnChooser: {
            container: 'body',
            position: {
                my: 'right top',
                at: 'right bottom',
                of: '.dx-datagrid-column-chooser-button',
                collision: 'flipfit',
                offset: '0 4',
                boundaryOffset: '8 8',
            },
        },
        // Standardized paging/pager baseline (đồng nhất pager toàn app). Per-page config still
        // overrides when a grid explicitly sets its own paging/pager.
        paging: { pageSize: 10 },
        pager: {
            visible: true,
            showPageSizeSelector: true,
            allowedPageSizes: [10, 50, 100, 500],
            showInfo: true,
            showNavigationButtons: true,
        },
    },
});

installVlpGridHeaderTooltips(document);

// Ghim container cho popup của MỌI widget dạng dropdown (menu action trong grid, select box,
// tag box, date box, drop down box).
//
// Lỗi được sửa: popup nhảy ra ngoài màn hình / chồng lên sidebar ở tất cả các trang.
// DevExtreme định vị overlay bằng hai transform lồng nhau và có 2 chế độ:
//   A (mặc định, không có container): wrapper được dời tới đúng chỗ cái nút/ô input, content mang
//      toạ độ TƯƠNG ĐỐI so với wrapper — vd wrapper translate(2434,623) + content translate(-200,32).
//   B (có container): wrapper đứng yên ở 0,0 phủ toàn viewport, content mang toạ độ TUYỆT ĐỐI.
// Trong app này overlay khởi tạo ở chế độ B rồi bị định vị lại sang chế độ A giữa chừng (đo được:
// đúng chỗ ở t+0, sai ở t+120ms), nhưng chỉ wrapper được dời còn content vẫn giữ toạ độ tuyệt đối
// ⇒ hai transform CỘNG DỒN: popup = vị trí đúng + toạ độ của nút, văng khỏi vùng nhìn thấy.
// (Nguồn gốc trong DevExtreme: OverlayPositionController.positionContent() — khi
// `_shouldRenderContentInitialPosition` = false nó `move($content, _visualPosition)` tức DÙNG LẠI
// toạ độ đo được ở lần render trước thay vì tính lại theo wrapper mới.)
// Khai báo container tường minh khoá `_getVisualContainer()` vào <body> nên wrapper không bao giờ
// bị dời nữa ⇒ không thể cộng dồn.
//
// defaultOptions deep-merge với dropDownOptions khai báo tại từng chỗ dùng (đã kiểm chứng bằng
// Chromium: `width`/`maxHeight` khai báo trên instance vẫn giữ nguyên, vị trí popup không đổi một
// pixel nào), nên không cần sửa ~100 file .html đang dùng các widget này.
//
// LƯU Ý: phải đăng ký trên TỪNG lớp cụ thể — đã đo: đăng ký trên lớp cơ sở DropDownEditor KHÔNG
// lan xuống SelectBox/DateBox/TagBox/DropDownBox.
[SelectBox, TagBox, DateBox, DropDownBox].forEach((widget) => {
    widget.defaultOptions({
        options: {
            dropDownOptions: { container: 'body' },
        },
    });
});

// SL-1368 — danh sách dài (tỉnh, phường/xã, nhân sự, nhóm) đổ thẳng xuống hết trang: mục cuối nằm dưới
// mép dưới khung nhìn, cuộn trang thì dropdown đóng lại, nên có những mục KHÔNG cách nào chọn được.
// DevExtreme tự tính chiều cao theo khoảng trống còn lại, nhưng dropdown đã bị ghim container vào <body>
// (khối ngay trên) nên phép tính đó không còn khớp với vị trí thật của ô chọn.
//
// Chặn cứng bằng maxHeight để dropdown tự cuộn bên trong. Dùng đơn vị theo khung nhìn thay vì px cố định
// để màn to vẫn hiện được nhiều mục. KHÔNG áp cho DateBox: dropdown của nó là tờ lịch (~300px, không cuộn
// được), cắt đi là mất hàng ngày cuối tháng trên cửa sổ thấp.
[SelectBox, TagBox, DropDownBox].forEach((widget) => {
    widget.defaultOptions({
        options: {
            dropDownOptions: { maxHeight: '50vh' },
        },
    });
});

// SL-1368 — nhãn dạng "mã — tên" (nhóm khách hàng, nhóm vận chuyển, đơn vị, nhân sự, điểm lấy hàng)
// dài hơn bề ngang ô chọn, mà dropdown mặc định rộng đúng bằng ô: DevExtreme cắt mất phần đuôi
// (`.dx-list-item-content { text-overflow: ellipsis; white-space: nowrap }`) nên hai lựa chọn khác nhau
// hiện ra y như nhau — "wce_anphat_hn — Công ty A…" và người dùng không biết chọn cái nào.
//
// wrapItemText là option CÓ SẴN của DropDownList: nó thêm lớp .dx-wrap-item-text để nhãn xuống dòng
// thay vì bị cắt. Đặt ở đây thay vì sửa từng chỗ dùng vì đây là lỗi CHUNG của mọi ô chọn trong app;
// bề ngang không đổi một pixel nào nên không xô lệch layout màn nào, mục nào dài thì cao thêm một dòng.
//
// CHỈ áp cho SelectBox/TagBox (và lookup của DataGrid — cũng là SelectBox): DateBox/DropDownBox kế thừa
// DropDownEditor chứ không phải DropDownList, không có option này.
[SelectBox, TagBox].forEach((widget) => {
    widget.defaultOptions({
        options: {
            wrapItemText: true,
        },
    });
});

DropDownButton.defaultOptions({
    options: {
        displayExpr: popupMenuItemText,
        dropDownOptions: { container: 'body' },
    },
});

// Every popup keeps a persistent title-bar close affordance, supports title-bar dragging and is
// clamped to the viewport. Feature templates still own their semantic footer actions and dirty-state
// guards through onHiding/visibleChange.
installVlpPopupDefaults();

// DevExtreme Popup does not expose a closeOnEscape option. Stop Escape only
// while a popup overlay is visible; dropdowns, popovers and load panels keep
// their native keyboard behaviour.
document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;

    const visiblePopup = document.querySelector(
        '.dx-popup-wrapper:not(.dx-state-invisible) .dx-overlay-content'
    );
    if (!visiblePopup) return;

    event.preventDefault();
    event.stopImmediatePropagation();
}, true);

bootstrapApplication(AppComponent, appConfig)
    .catch(err => console.error(err));
